import {
  getFirestore,
  collection,
  query,
  where,
  orderBy,
  getDocs,
  limit,
} from 'firebase/firestore'
import { handleErrors } from '../utils/errorHandler'

// bcst_lesson_progress shape: { lessonId, courseId, userId, completed, percentComplete, ... }

// Firestore caps the 'in' operator at 30 values per query; batch queries are chunked to this size.
const FIRESTORE_IN_QUERY_LIMIT = 30

export const TransformLearningService = () => {
  const db = getFirestore()

  async function getActiveCourse() {
    try {
      const q = query(
        collection(db, 'bcst_courses'),
        where('status', '==', 'published'),
        limit(1)
      )
      const snap = await getDocs(q)
      if (snap.empty) return null
      const d = snap.docs[0]
      return { id: d.id, ...d.data() }
    } catch (error) {
      handleErrors('TransformLearningService.getActiveCourse', error)
      throw error
    }
  }

  async function getModulesByCourse(courseId) {
    try {
      const q = query(
        collection(db, 'bcst_modules'),
        where('courseId', '==', courseId),
        orderBy('position')
      )
      const snap = await getDocs(q)
      return snap.docs.map(d => ({ id: d.id, ...d.data() }))
    } catch (error) {
      handleErrors(`TransformLearningService.getModulesByCourse-${courseId}`, error)
      throw error
    }
  }

  // Fetches published lessons for a course ordered by position.
  // The store groups them by moduleId after fetch (one query vs N per module).
  // Draft (or otherwise non-published) lessons must never surface to end users, so
  // status is filtered in the query — this avoids transmitting draft content to the
  // client at all (backed by the courseId + status + position composite index).
  async function getLessonsByCourse(courseId) {
    try {
      const q = query(
        collection(db, 'bcst_lessons'),
        where('courseId', '==', courseId),
        where('status', '==', 'published'),
        orderBy('position')
      )
      const snap = await getDocs(q)
      return snap.docs.map(d => ({ id: d.id, ...d.data() }))
    } catch (error) {
      handleErrors(`TransformLearningService.getLessonsByCourse-${courseId}`, error)
      throw error
    }
  }

  async function getProgressByCourse(courseId, userId) {
    try {
      const q = query(
        collection(db, 'bcst_lesson_progress'),
        where('courseId', '==', courseId),
        where('userId', '==', userId)
      )
      const snap = await getDocs(q)
      return snap.docs.map(d => ({ id: d.id, ...d.data() }))
    } catch (error) {
      handleErrors(`TransformLearningService.getProgressByCourse-${courseId}`, error)
      throw error
    }
  }

  // Splits an id list into chunks of Firestore's 'in'-query limit.
  function chunkIds(ids) {
    const chunks = []
    for (let i = 0; i < ids.length; i += FIRESTORE_IN_QUERY_LIMIT) {
      chunks.push(ids.slice(i, i + FIRESTORE_IN_QUERY_LIMIT))
    }
    return chunks
  }

  // Batch-fetch assessments for a set of quiz-lesson IDs, chunked to Firestore's 'in' limit.
  // Chunks are queried in parallel so latency does not grow linearly with the catalog size.
  async function getAssessmentsByLessonIds(lessonIds) {
    if (!lessonIds.length) return []
    try {
      const snaps = await Promise.all(
        chunkIds(lessonIds).map(chunk =>
          getDocs(query(collection(db, 'bcst_assessments'), where('lessonId', 'in', chunk)))
        )
      )
      return snaps.flatMap(snap => snap.docs.map(d => ({ id: d.id, ...d.data() })))
    } catch (error) {
      handleErrors('TransformLearningService.getAssessmentsByLessonIds', error)
      throw error
    }
  }

  // Batch-count quiz questions grouped by assessmentId.
  // Returns { [assessmentId]: number }.
  async function getQuizQuestionCountsByAssessmentIds(assessmentIds) {
    if (!assessmentIds.length) return {}
    try {
      const snaps = await Promise.all(
        chunkIds(assessmentIds).map(chunk =>
          getDocs(query(collection(db, 'bcst_quiz_questions'), where('assessmentId', 'in', chunk)))
        )
      )
      const counts = {}
      for (const snap of snaps) {
        snap.docs.forEach(d => {
          const aid = d.data().assessmentId
          counts[aid] = (counts[aid] ?? 0) + 1
        })
      }
      return counts
    } catch (error) {
      handleErrors('TransformLearningService.getQuizQuestionCountsByAssessmentIds', error)
      throw error
    }
  }

  return {
    getActiveCourse,
    getModulesByCourse,
    getLessonsByCourse,
    getProgressByCourse,
    getAssessmentsByLessonIds,
    getQuizQuestionCountsByAssessmentIds,
  }
}
